//	some api endpoints (eg. verify-otp) return an HttpOnly jwt cookie. 
//	that cookie is automatically stored by the browser, 
//	in a way such that it's not accessible to frontend code, 
//	but is sent to the backend in subsequent fetch() calls that have `credentials: 'include'`. 
//	we can use this to check if the user is authenticated.
//	/api/v1/me checks the jwt cookie and returns the user's session info if the cookie is valid, 
//	otherwise it returns a 401 unauthorized error.

import { useCallback, useEffect, useState } from 'react'
import * as env from '@/env'
import { useUserStore } from './user-store'
import { useChatStore } from './chat-store'
import { resetFeathersClient } from '@/pages/chat/feathers-client'

type Session = {
	userId: string
	email: string
	//	ha-203: true when the logged-in account (= JWT userId) has an active impersonation
	//	flag set on the user record. drives the impersonation banner in the chat UI.
	isImpersonating?: boolean
}

export function useSession() {
	const [session, setSession] = useState<Session | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	
	const clearUser = useUserStore(state => state.clearUser)
	const clearChat = useChatStore(state => state.clearChat)

	const clear = useCallback(() => {
		setSession(null)
		clearUser()
		clearChat()
		resetFeathersClient()
	}, [clearUser, clearChat])

	//	re-check the session against the server. callers should await this after
	//	any flow that sets the auth cookie (eg. OTP verification) before navigating
	//	to authenticated routes, so the React session state catches up with the cookie.
	const refreshSession = useCallback(async () => {
		try {
			const res = await fetch(`${env.restBaseUrl}/api/v1/me`, {
				method: 'GET',
				credentials: 'include',  // send cookies
			})

			if (!res.ok) {
				if (res.status === 401) {
					//	on 401, the cookie sent in the request was rejected (stale/invalid/expired).
					//	cookies are HttpOnly, so JS can't delete them — call /logout to have the server
					//	emit Set-Cookie clearing headers, otherwise the stale cookie keeps producing
					//	401s on every reload and the user is stuck appearing logged-out.
					//	best-effort: if /logout fails, React state is still cleared (same caveat as
					//	the explicit logout() callback below).
					try {
						await fetch(`${env.restBaseUrl}/api/v1/logout`, {
							method: 'POST',
							credentials: 'include',
						})
					} catch (e) {
						console.error('Failed to clear stale auth cookie via /logout:', e)
					}
					clear()
				} else {
					//	5xx / other non-401: transient backend trouble. don't bounce the user out
					//	of their React-side session -- the next natural refreshSession call will
					//	retry. only 401 is a definitive auth-failure signal from /me.
					console.warn(`/me returned non-OK status ${res.status} -- leaving session state intact`)
				}
			} else {
				const data = await res.json()
				setSession(data)
			}
		} catch (err) {
			//	network error (browser timeout, offline, DNS). same reasoning as the 5xx branch
			//	above: transient, don't bounce the user. they'll retry naturally on the next
			//	refreshSession call.
			console.error('Failed to check session (network error -- leaving session state intact):', err)
		} finally {
			setIsLoading(false)
		}
	}, [clear])

	useEffect(
		() => {
			refreshSession()
		},
		[refreshSession])
	
	const logout = useCallback(async () => {
		try {
			const res = await fetch(`${env.restBaseUrl}/api/v1/logout`, {
				method: 'POST',
				credentials: 'include',
			})

			if (!res.ok)
				throw new Error('!res.ok')
		} catch (err) {
			//	always clear local session even if server-side logout failed
			//	(the cookie may persist until it expires naturally, but the React state is clean)
			console.error('Failed to logout:', err)
		} finally {
			clear()
		}
	}, [clear])

	return { session, isLoading, logout, refreshSession }
}
